/**
 * Arranging a card grid: the pointer half. The one arrange implementation, driving the Site
 * Analytics grid (livewire/sites/analytics.blade.php) and a report's open tab
 * (livewire/report/show.blade.php) alike, through the markers both pages' cards carry:
 * `data-site-analytics-grid` on the grid, and on each card `data-site-analytics-card` (its id),
 * data-span / data-min-span, and data-height-rows / data-min-rows / data-max-rows: the whole rows of
 * the page grid its stored height takes, and their bounds.
 *
 * What a height is differs by page, and a resize writes both variables a page may read. On a report
 * the height is the card's: it spans those rows (--sa-card-rows) and its chart fills them. On Site
 * Analytics it is the chart's (--sa-card-height), and the card measures the rows its content needs
 * itself (x-fit-rows); such a card carries `data-rows-measured`, and --sa-card-rows is never written
 * on it, since that is its measurement's to set.
 *
 * The browser applies a drag or a resize at once; the Livewire call that follows records it, and
 * both pages' calls re-render (resizing without a render was tried on the site page and reverted).
 * Between the change on screen and the render that confirms it, the chrome the server drew (a
 * card's "12/12" chip, its "Card 3 of 8", whether its move buttons are disabled) describes where
 * the cards were, so this file updates that chrome itself, over the whole grid rather than the card
 * that moved.
 *
 * Card ids are read off the card element (arr_cardOf()), never written into an expression: an id is
 * stored free text. A caller may still pass one, as the site page's markup does; it is ignored in
 * favour of the element's own.
 *
 * The span classes are handed down from the server (each page's spanClassMap) instead of being
 * spelled here, because two copies of that map drift the first time a breakpoint moves.
 *
 * Options (arr_init's second argument):
 *  - alignGuides: light the guides from the column the card starts in, not from the first, so a
 *    card on the right of a row shows its width where it is;
 *  - resizeMinWidth: the narrowest viewport, in px, a pointer resize starts at. Both pages' spans
 *    are exact twelfths only from lg up, so below it a drag would move the chip and nothing on
 *    screen;
 *  - rows: the page grid's row, gap and card chrome in px (RowGrid::clientOptions()), which a height
 *    resize snaps to and turns back into the body height the server stores. Without them a height
 *    grip resizes nothing;
 *  - pinned: a selector for the headers the page pins over the top of the grid's scroll area (each
 *    page's edit bar and filter section). A drag's top edge is then their bottom, not the scroll
 *    area's own top, which they cover, and a drag let go over them is put back where it started:
 *    see arr_watchPinned(). '' leaves a drag to SortableJS and the browser alone.
 *
 * Both pages' arrange menus close the moment something is grabbed to drag, on GRAB_EVENT, which
 * this module raises (see arr_grabbed()).
 */

/**
 * Raised on window the moment the editor grabs something to drag: presses a card's handle, a palette
 * entry or a resize grip. Its detail.item is what was grabbed: the card or palette entry SortableJS
 * chose, or the card being resized.
 *
 * Every arrange menu closes on it: each card's Size and Move (and a report card's Rows and Move to
 * tab), the "⋯" of a folded toolbar, and the tab options. Only a press on one of the Add chart
 * panel's own entries does not close the panel: it shrinks to that entry instead (see its markup).
 *
 * Why the press and not the drag:
 * - The menus close on a click outside, and a drag never makes one. A native drag ends in a drop,
 *   not a click, and SortableJS's fallback swallows the next click itself.
 * - The editor has grabbed the moment they press. SortableJS chooses the item on the press, before
 *   the pointer moves, so waiting for the drag leaves a menu open while they hold still.
 */
export const GRAB_EVENT = 'arrange-grab';

/**
 * SortableJS's own DOM event for the press that chooses an item, fired before the pointer moves. It
 * bubbles from the item's list and is heard on window, so the palette's lists count too: they sit
 * outside the grid this scope is started on.
 */
const SORTABLE_CHOOSE_EVENT = 'choose';

/**
 * Raised on window when a drag is let go over the page's pinned headers (the `pinned` option) and
 * has been put back where it started: a card to its place in the grid, a palette entry into its
 * list. Its detail holds the item and the list it came from. Raised before SortableJS's own `end`
 * for that drag, so a page can tell that this end added and moved nothing (report-page.js keeps its
 * Add chart panel open for it).
 */
export const DROP_CANCEL_EVENT = 'arrange-drop-cancel';

/** How far under the pinned headers, in px, a drag held there scrolls the page up. */
const PINNED_SCROLL_ZONE = 48;

/** The most the page scrolls up per frame: at the zone's top edge and over the headers. */
const PINNED_SCROLL_MAX_STEP = 16;

/**
 * The headers pinned at the top of a scroll area, from their viewport rectangles: taken from the
 * top down, each counts only while it sits on the one above it, or on the area's top edge. That is
 * where a sticky header sits once it is stuck; a header still in the page's flow (the filter card
 * before the page scrolls it up) sits lower and is not pinned. Returns the stack's box (bottom is
 * the edge a drag's view of the grid starts at), or null when nothing is pinned.
 *
 * @param {number} top the scroll area's top edge in the viewport
 * @param {Array<{top: number, bottom: number, left: number, right: number, height: number}>} rects
 * @returns {{top: number, bottom: number, left: number, right: number}|null}
 */
function pinnedStack(top, rects) {
    let stack = null;

    for (const rect of [...rects].filter((box) => box.height > 0).sort((a, b) => a.top - b.top)) {
        const edge = stack ? stack.bottom : top;

        // A pixel of slack: a sticky top is a whole-pixel height, the rect above it may not be.
        if (rect.top > edge + 1) break;

        stack = stack
            ? { top: stack.top, bottom: Math.max(stack.bottom, rect.bottom), left: Math.min(stack.left, rect.left), right: Math.max(stack.right, rect.right) }
            : { top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right };
    }

    return stack;
}

/**
 * How far to scroll the page up this frame for a drag held at `y`, with the pinned headers ending at
 * `edge`: nothing below the zone under them, more the higher the pointer, the most over the headers.
 */
function pinnedScrollStep(y, edge) {
    const depth = (edge + PINNED_SCROLL_ZONE - y) / PINNED_SCROLL_ZONE;

    return depth > 0 ? Math.ceil(PINNED_SCROLL_MAX_STEP * Math.min(1, depth)) : 0;
}

/** The element that scrolls the grid: its nearest scrolling ancestor, else the document's. */
function scrollerOf(element) {
    for (let node = element?.parentElement; node; node = node.parentElement) {
        if (/(auto|scroll)/.test(getComputedStyle(node).overflowY) && node.scrollHeight > node.clientHeight) return node;
    }

    return document.scrollingElement ?? document.documentElement;
}

export default function SiteAnalyticsArrange() {
    /** The window listener arr_init() adds, kept so destroy() can remove it. */
    let onSortableChoose = null;

    /** The window listeners arr_watchPinned() adds ([name, listener, options]), for destroy(). */
    let pinnedListeners = null;

    /**
     * The drag in flight while the page pins headers: what was dragged, the list and the node it
     * came from, where the pointer is, the element that scrolls and the auto-scroll's frame. Kept
     * out of the component's reactive state, since the pointer is written on every dragover.
     */
    let drag = null;

    return {
        /** span => class string, from the server. */
        arr_spanClasses: {},

        arr_options: { alignGuides: false, resizeMinWidth: 0, rows: null, pinned: '' },

        /** The grid element this scope sits on, kept from arr_init(). */
        arr_root: null,

        /** The resize in flight, or null. */
        arr_resizing: null,

        arr_init(spanClasses, options = {}) {
            this.arr_spanClasses = spanClasses || {};
            this.arr_options = {
                alignGuides: options.alignGuides === true,
                resizeMinWidth: Number(options.resizeMinWidth) || 0,
                rows: Number(options.rows?.rowPx) > 0
                    ? {
                        rowPx: Number(options.rows.rowPx),
                        gapPx: Number(options.rows.gapPx) || 0,
                        chromePx: Number(options.rows.chromePx) || 0,
                    }
                    : null,
                pinned: typeof options.pinned === 'string' ? options.pinned : '',
            };
            // x-init runs on the grid itself, so $el here is the grid.
            this.arr_root = this.$el?.hasAttribute?.('data-site-analytics-grid') ? this.$el : null;

            if (onSortableChoose === null) {
                onSortableChoose = (event) => this.arr_grabbed(event.item);
                window.addEventListener(SORTABLE_CHOOSE_EVENT, onSortableChoose);
            }

            if (this.arr_options.pinned !== '') this.arr_watchPinned();
        },

        /**
         * Alpine calls this when the grid leaves the page. A grid Livewire replaced would otherwise
         * keep listening, and every later grab would be announced once for each grid the page had.
         */
        destroy() {
            if (onSortableChoose !== null) {
                window.removeEventListener(SORTABLE_CHOOSE_EVENT, onSortableChoose);
                onSortableChoose = null;
            }

            pinnedListeners?.forEach(([name, listener, options]) => window.removeEventListener(name, listener, options));
            pinnedListeners = null;

            if (drag) cancelAnimationFrame(drag.frame);
            drag = null;
        },

        /**
         * A page that pins headers over the top of the grid's scroll area (the `pinned` option) hides
         * the edge a drag is measured against. SortableJS and the browser scroll a drag held within
         * a few px of the scroll area's own top, which lies under those headers, so a card or palette
         * entry held at the top of the cards the editor can see never scrolled the page, and one let
         * go on the headers landed wherever SortableJS last placed it: often a row the headers cover,
         * out of sight. So, for a card of this grid and a palette entry (a `palette:` key, see
         * arr_onSort()) alike:
         *  - held in the zone under the pinned headers, or over them, the page scrolls up
         *    (arr_pinnedScroll());
         *  - let go over one of those headers, pinned or not yet (the scroll can bring the page to
         *    the top, where the filter card rejoins the page's flow, under the same pointer), the
         *    drag is put back where it started before SortableJS reads where it ended, so nothing is
         *    added or moved (arr_pinnedRelease()).
         * The headers are measured on every frame, since the filter card folds and unfolds as the page
         * scrolls. Listened for on window in the capture phase: SortableJS stops a dragover at the
         * list it is over, and the release must be seen before its own handlers run.
         */
        arr_watchPinned() {
            if (pinnedListeners !== null) return;

            const start = (event) => this.arr_pinnedStart(event);
            const end = () => this.arr_pinnedEnd();
            const track = (event) => this.arr_pinnedTrack(event);
            const release = (event) => this.arr_pinnedRelease(event);

            pinnedListeners = [
                ['start', start, false],
                ['end', end, false],
                ['dragover', track, true],
                ['pointermove', track, true],
                ['touchmove', track, { capture: true, passive: true }],
                ['drop', release, true],
                ['dragend', release, true],
                ['pointerup', release, true],
                ['touchend', release, true],
            ];
            pinnedListeners.forEach(([name, listener, options]) => window.addEventListener(name, listener, options));
        },

        /** SortableJS's `start`: a drag of this grid's card or of a palette entry is followed. */
        arr_pinnedStart(event) {
            const item = event?.item;
            const from = event?.from;
            const grid = this.arr_grid();
            const key = item?._x_sort_key;

            if (! item || ! from || ! grid) return;

            if (from !== grid && ! (typeof key === 'string' && key.startsWith('palette:'))) return;

            if (drag) cancelAnimationFrame(drag.frame);

            drag = { item, from, next: item.nextSibling, x: null, y: null, scroller: scrollerOf(grid), frame: 0, isReleased: false };
            drag.frame = requestAnimationFrame(() => this.arr_pinnedScroll());
        },

        /** SortableJS's `end`: the drag is over, however it ended. */
        arr_pinnedEnd() {
            if (drag) cancelAnimationFrame(drag.frame);

            drag = null;
        },

        arr_pinnedTrack(event) {
            if (! drag) return;

            const point = event.touches?.[0] ?? event;

            drag.x = point.clientX;
            drag.y = point.clientY;
        },

        /** The `pinned` headers' viewport rectangles right now. */
        arr_pinnedRects() {
            return [...document.querySelectorAll(this.arr_options.pinned)].map((element) => element.getBoundingClientRect());
        },

        /** The box of the headers pinned right now (pinnedStack()), or null when none is. */
        arr_pinnedBox() {
            const scroller = drag?.scroller;
            const isDocument = ! scroller || scroller === document.scrollingElement || scroller === document.documentElement;
            const top = isDocument ? 0 : scroller.getBoundingClientRect().top;

            return pinnedStack(top, this.arr_pinnedRects());
        },

        /**
         * One frame of the drag: while the pointer is over the pinned headers, or in the zone under
         * them, within their width, the page scrolls up (pinnedScrollStep()). The browser's own
         * dragover, and SortableJS's with it, then places the item among the cards the scroll brings
         * under the pointer.
         */
        arr_pinnedScroll() {
            if (! drag) return;

            drag.frame = requestAnimationFrame(() => this.arr_pinnedScroll());

            if (drag.isReleased || drag.y === null || drag.scroller.scrollTop <= 0) return;

            const box = this.arr_pinnedBox();

            if (! box || drag.x < box.left || drag.x > box.right) return;

            const step = pinnedScrollStep(drag.y, box.bottom);

            if (step > 0) drag.scroller.scrollTop -= step;
        },

        /**
         * The drag let go (a native drag's drop or dragend, a touch or fallback drag's pointerup or
         * touchend), before SortableJS handles it. Over one of the `pinned` headers, the item goes
         * back to the node it started before, in the list it came from, as SortableJS's own
         * revertOnSpill puts an item back: SortableJS then finds it where it started and records
         * nothing, and a palette entry's clone is taken away. Anywhere else the drop is SortableJS's,
         * as it was.
         */
        arr_pinnedRelease(event) {
            if (! drag || drag.isReleased) return;

            // A native drag's end is placed by the last dragover; a touch's by the finger that lifted.
            const point = event.changedTouches?.[0] ?? (event.type.startsWith('drag') || event.type === 'drop' ? null : event);
            const x = point ? point.clientX : drag.x;
            const y = point ? point.clientY : drag.y;

            if (x === null || y === null) return;

            const isOverHeader = this.arr_pinnedRects().some((rect) => x >= rect.left && x <= rect.right && y >= rect.top && y < rect.bottom);

            if (! isOverHeader) return;

            const { item, from, next } = drag;

            drag.isReleased = true;

            if (item.parentNode !== from || item.nextSibling !== next) {
                if (next && next.parentNode === from) {
                    from.insertBefore(item, next);
                } else {
                    from.appendChild(item);
                }
            }

            window.dispatchEvent(new CustomEvent(DROP_CANCEL_EVENT, { detail: { item, from } }));
        },

        /** Tell the arrange menus that `item` was grabbed to drag (GRAB_EVENT). */
        arr_grabbed(item) {
            window.dispatchEvent(new CustomEvent(GRAB_EVENT, { detail: { item } }));
        },

        /**
         * The grid this scope arranges: the element it was started on, else the page's one grid.
         *
         * NOT `this.$el` at call time: inside a method invoked from an event on a child, Alpine's $el
         * is that child (a 24px grip). Measuring a column against it made one column about one
         * pixel wide, so the smallest movement of the pointer jumped the card across half the row.
         */
        arr_grid() {
            if (this.arr_root?.isConnected) return this.arr_root;

            return document.querySelector('[data-site-analytics-grid]');
        },

        /** Every card element in the grid, in DOM order. */
        arr_cards() {
            const grid = this.arr_grid();

            return grid ? [...grid.querySelectorAll('[data-site-analytics-card]')] : [];
        },

        /** The id of the card an element sits in, read off the card; '' outside any card. */
        arr_cardOf(el) {
            return el?.closest?.('[data-site-analytics-card]')?.getAttribute('data-site-analytics-card') ?? '';
        },

        /** The column guides: the grid's sibling in their shared positioned parent. */
        arr_guides() {
            return this.arr_gridSibling('data-site-analytics-guides');
        },

        /** The row guides, the grid's other sibling there. */
        arr_rowGuides() {
            return this.arr_gridSibling('data-site-analytics-row-guides');
        },

        arr_gridSibling(marker) {
            const parent = this.arr_grid()?.parentElement;

            return parent ? [...parent.children].find((child) => child.hasAttribute?.(marker)) ?? null : null;
        },

        /** What a card grows by per row: the row and the gap before the next. */
        arr_rowStep() {
            const rows = this.arr_options.rows;

            return rows ? rows.rowPx + rows.gapPx : 0;
        },

        /**
         * The body height, in px, that fills exactly this many rows: what the server stores
         * (RowGrid::heightFor()), so a height written here reads back as the same rows.
         */
        arr_heightFor(rows) {
            return rows * this.arr_rowStep() - this.arr_options.rows.gapPx - this.arr_options.rows.chromePx;
        },

        /**
         * One grid column in px, measured rather than assumed: the grid is fluid, the sidebar
         * collapses, and the gap is a token that can change.
         */
        arr_columnWidth() {
            const grid = this.arr_grid();

            if (!grid || grid.clientWidth === 0) return { width: 0, gap: 0 };

            const gap = parseFloat(window.getComputedStyle(grid).columnGap) || 0;

            return { width: (grid.clientWidth - gap * 11) / 12, gap };
        },

        /**
         * Start a resize on one axis: 'x' (width), 'y' (height) or 'xy' (both, from the corner).
         * Called as (event, axis), or as (event, cardId, axis) by markup that still passes an id.
         * Height moves in whole rows of the page grid, so without the rows option a 'y' grip does
         * nothing and the corner resizes width only.
         *
         * Pointer capture, so the drag survives the pointer leaving the grip: without it a fast
         * drag stops resizing the moment it outruns a 24px target, which reads as the feature being
         * broken rather than the pointer having slipped.
         */
        arr_startResize(event, cardIdOrAxis, maybeAxis) {
            const asked = String(maybeAxis === undefined ? cardIdOrAxis : maybeAxis);
            const axis = this.arr_options.rows ? asked : asked.replace('y', '');
            const card = event.target.closest('[data-site-analytics-card]');

            if (!card || axis === '') return;

            if (this.arr_options.resizeMinWidth > 0 && window.innerWidth < this.arr_options.resizeMinWidth) return;

            event.preventDefault();
            event.stopPropagation();
            event.target.setPointerCapture?.(event.pointerId);

            const { width, gap } = this.arr_columnWidth();

            // A grid that measures zero means the page is mid-layout; a resize against it would
            // step a column per pixel. Better to do nothing than to move the card wildly.
            if (width <= 0) return;

            const columnWidth = width + gap;
            const startSpan = Number(card.dataset.span) || 12;
            const startRows = Number(card.dataset.heightRows) || 1;
            const minRows = Number(card.dataset.minRows) || 1;
            const measured = card.hasAttribute('data-rows-measured');

            this.arr_resizing = {
                id: this.arr_cardOf(card),
                axis,
                element: card,
                startX: event.clientX,
                startY: event.clientY,
                startSpan,
                startRows,
                minSpan: Number(card.dataset.minSpan) || 1,
                minRows,
                maxRows: Math.max(minRows, Number(card.dataset.maxRows) || startRows),
                measured,
                // The rows the card spans now. A measured card's are its content's, and a row more
                // of height is a row more of card, since only the chart grows.
                startCardRows: (measured && Number(card.style.getPropertyValue?.('--sa-card-rows'))) || startRows,
                columnWidth,
                startColumn: this.arr_options.alignGuides ? this.arr_startColumn(card, columnWidth, startSpan) : 0,
            };

            this.arr_resizing.span = this.arr_resizing.startSpan;
            this.arr_resizing.rows = this.arr_resizing.startRows;

            // A resize is a grab too, and SortableJS raises nothing for it, so a menu left open would
            // hang over the card being resized.
            this.arr_grabbed(card);

            document.body.classList.add('sa-resizing');

            // The guides for each axis being resized, with the card's own span or rows lit up
            // inside them, for as long as the pointer is down. A resize without them is a guess
            // about where the twelfths and the rows are.
            if (axis.includes('x')) this.arr_showGuides(startSpan, card);
            if (axis.includes('y')) this.arr_showRowGuides(this.arr_resizing.startCardRows, card);
        },

        /**
         * The column a card starts in, from where it sits in the grid. A card widened past the
         * row's end would wrap, so the span it may grow to stops at the row's last column.
         */
        arr_startColumn(card, columnWidth, span) {
            const grid = this.arr_grid();
            const offset = card.offsetLeft - (grid?.offsetLeft ?? 0);
            const column = columnWidth > 0 ? Math.round(offset / columnWidth) : 0;

            return Math.max(0, Math.min(12 - Math.min(12, span), column));
        },

        /** Pointer travel, in px, before the first column step. Stops a click from resizing. */
        arr_deadZone: 14,

        arr_onResizeMove(event) {
            const state = this.arr_resizing;

            if (!state) return;

            if (state.axis.includes('x')) {
                const travelled = event.clientX - state.startX;

                // Inside the dead zone the card keeps the width it had: a press that wobbles is a
                // press, not a resize.
                const steps = Math.abs(travelled) < this.arr_deadZone
                    ? 0
                    : Math.round(travelled / state.columnWidth);

                const span = Math.max(state.minSpan, Math.min(12 - state.startColumn, state.startSpan + steps));

                if (span !== state.span) {
                    // Swap for the class the server would render, so the card really is this wide
                    // rather than being transformed into looking it.
                    const previous = this.arr_spanClasses[state.span];
                    const next = this.arr_spanClasses[span];

                    if (previous) state.element.classList.remove(...previous.split(' '));
                    if (next) state.element.classList.add(...next.split(' '));

                    state.span = span;
                    state.element.dataset.span = String(span);
                    this.arr_updateSizeChip(state.element, span);
                    this.arr_showGuides(span, state.element);

                    // A new width can move the card to another row, and narrows its rows' guides.
                    if (state.axis.includes('y')) this.arr_showRowGuides(this.arr_cardRows(state), state.element);
                }
            }

            if (state.axis.includes('y')) {
                // Whole rows, as the width moves in whole columns. A row is 52px, so the first step
                // takes half of one, which is already past the dead zone a press has to wobble by.
                const steps = Math.round((event.clientY - state.startY) / this.arr_rowStep());

                const rows = Math.max(state.minRows, Math.min(state.maxRows, state.startRows + steps));

                if (rows !== state.rows) {
                    state.rows = rows;
                    state.element.dataset.heightRows = String(rows);
                    // The chart's height on Site Analytics, whose card then measures its new rows;
                    // the card's rows on a report, whose chart fills them. Either way the chart
                    // host redraws when its box settles.
                    state.element.style.setProperty('--sa-card-height', `${this.arr_heightFor(rows)}px`);
                    if (!state.measured) state.element.style.setProperty('--sa-card-rows', String(rows));
                    this.arr_showRowGuides(this.arr_cardRows(state), state.element);

                    // The column guides run the card's height.
                    if (state.axis.includes('x')) this.arr_showGuides(state.span, state.element);
                }
            }
        },

        /**
         * Commit once, on release. A drag that wanders across six widths is one write and one
         * announcement, not six. Idempotent: pointerup and lostpointercapture both land here.
         */
        arr_endResize() {
            const state = this.arr_resizing;

            this.arr_resizing = null;
            document.body.classList.remove('sa-resizing');
            this.arr_hideGuides();

            if (!state) return;

            const rowsChanged = state.rows !== state.startRows;

            if (state.span === state.startSpan && !rowsChanged) return;

            // A height only when the rows moved: a card whose stored height falls between rows keeps
            // it through a change of width.
            this.$wire.resizeCard(state.id, state.span, rowsChanged ? this.arr_heightFor(state.rows) : null);
        },

        /**
         * Light the column guides, with the span the card currently occupies filled in.
         *
         * The guides live in an overlay that is a SIBLING of the grid, never a child: the grid's
         * children are reconciled by Livewire's morph against the server's card list, and anything
         * else in there is an element the morph has no counterpart for.
         */
        arr_showGuides(span, card = null) {
            const guides = this.arr_guides();

            if (!guides) return;

            // Hug the card being resized. The overlay's parent is the grid's positioned wrapper, so
            // the card's offset within it is the top; without this the guides run the whole page
            // and twelve stripes cover every chart on it.
            if (card) {
                guides.style.top = `${card.offsetTop}px`;
                guides.style.height = `${card.offsetHeight}px`;
            }

            guides.classList.add('sa-guides-visible');

            const start = this.arr_resizing?.startColumn ?? 0;

            [...guides.children].forEach((cell, index) => {
                cell.classList.toggle('sa-guide-active', index >= start && index < start + span);
            });

            const readout = guides.querySelector('[data-guide-readout]');

            if (readout) readout.textContent = `${span}/12`;
        },

        /** How many rows past the card's own the row guides show, so there is somewhere to drag to. */
        arr_rowsAhead: 3,

        /** The rows the card being resized spans, from how far its height has moved. */
        arr_cardRows(state) {
            return state.startCardRows + state.rows - state.startRows;
        },

        /**
         * Light the row guides under the card being resized: the rows it spans lit, a few more after
         * them up to the most it may take, and the count at the foot of the lit rows. Laid over the
         * card's own columns, from its top.
         */
        arr_showRowGuides(rows, card) {
            const guides = this.arr_rowGuides();

            if (!guides || !card) return;

            guides.style.top = `${card.offsetTop}px`;
            guides.style.left = `${card.offsetLeft}px`;
            guides.style.width = `${card.offsetWidth}px`;
            guides.classList.add('sa-row-guides-visible');

            const state = this.arr_resizing;
            const most = state ? state.startCardRows + state.maxRows - state.startRows : rows;
            const shown = Math.min(most, rows + this.arr_rowsAhead);

            [...guides.children]
                .filter((child) => child.classList.contains('sa-guide'))
                .forEach((cell, index) => {
                    cell.classList.toggle('sa-guide-active', index < rows);
                    cell.classList.toggle('hidden', index >= shown);
                });

            const readout = guides.querySelector('[data-row-guide-readout]');

            if (readout) {
                const template = (rows === 1 ? readout.dataset.one : readout.dataset.other) ?? ':rows';

                readout.textContent = template.replace(':rows', String(rows));
                readout.style.top = `${rows * this.arr_rowStep() - (this.arr_options.rows?.gapPx ?? 0)}px`;
            }
        },

        arr_hideGuides() {
            const guides = this.arr_guides();

            if (guides) {
                guides.classList.remove('sa-guides-visible');
                [...guides.children].forEach((cell) => cell.classList.remove('sa-guide-active'));
            }

            const rowGuides = this.arr_rowGuides();

            if (rowGuides) {
                rowGuides.classList.remove('sa-row-guides-visible');
                [...rowGuides.children].forEach((cell) => cell.classList.remove('sa-guide-active'));
            }
        },

        /** The card's own "9/12" chip, which the server drew and has not drawn again yet. */
        arr_updateSizeChip(card, span) {
            const chip = card.querySelector('[data-size-chip]');

            if (chip) chip.textContent = `${span}/12`;
        },

        /**
         * Both kinds of drop, because SortableJS calls the RECEIVING container's handler either way
         * and the only difference is where the item came from.
         *
         * A palette chip carries a `palette:` key and is a clone: the real card is the one the
         * server renders at that position, and there is no card in the DOM yet to keep. The clone
         * is removed first, because a stray chip sitting in the grid for a beat reads as the card
         * having failed to draw.
         */
        arr_onSort(item, position) {
            if (typeof item === 'string' && item.startsWith('palette:')) {
                this.arr_grid()?.querySelectorAll('[data-palette-clone]').forEach((clone) => clone.remove());

                this.$wire.addCardAt(item.slice('palette:'.length), position);

                return;
            }

            // SortableJS has already moved the node. Renumber the chrome the server would have
            // renumbered, then record the move.
            this.arr_renumber();
            this.$wire.moveCardDragged(item, position);
        },

        /**
         * Re-state each card's position and re-enable its move buttons after a client-side move.
         *
         * "Card 3 of 8" and the disabled state of the first and last card's buttons are drawn by
         * the server, and after a drag they describe where the cards used to be. A reader who drags
         * a card to the top and then presses its up button (still enabled, still pointing at a
         * position that no longer exists) is the bug this prevents.
         */
        arr_renumber() {
            const cards = this.arr_cards();
            const total = cards.length;

            cards.forEach((card, index) => {
                const position = card.querySelector('[data-card-position]');

                if (position) {
                    position.textContent = position.dataset.cardPosition
                        .replace(':position', String(index + 1))
                        .replace(':total', String(total));
                }

                const up = card.querySelector('[data-move-up]');
                const down = card.querySelector('[data-move-down]');

                if (up) up.disabled = index === 0;
                if (down) down.disabled = index === total - 1;
            });
        },
    };
}
